import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../../ui/dialog';
import { API_URLS } from '../../../config/api';
import { 
  Mail, 
  Plus, 
  Edit2, 
  Trash2, 
  Search, 
  Eye,
  Copy,
  RefreshCcw
} from 'lucide-react';
import { adminApiFetch } from '../../../utils/adminApi';
import DOMPurify from 'dompurify';

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    html_content: '',
    description: '',
    is_active: true
  });

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=getEmailTemplates`);
      const data = await response.json();
      if (data && Array.isArray(data)) {
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadForms = async () => {
    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=getForms`);
      const data = await response.json();
      if (data && Array.isArray(data)) {
        setForms(data);
      }
    } catch (error) {
      console.error('Error loading forms:', error);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadForms();
  }, []);

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name || '',
        subject: template.subject || '',
        html_content: template.html_content || '',
        description: template.description || '',
        is_active: template.is_active !== false
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        subject: '',
        html_content: '<h2>Merhaba {{name}},</h2>\n<p>Bu bir e-posta şablonudur.</p>\n<p>En iyi dileklerimizle,<br/>Website Al</p>',
        description: '',
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      html_content: '',
      description: '',
      is_active: true
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject) {
      alert('Lütfen şablon adı ve konu girişi yapın');
      return;
    }

    setSaving(true);
    try {
      const action = editingTemplate ? 'updateEmailTemplate' : 'createEmailTemplate';
      const payload = editingTemplate 
        ? { id: editingTemplate.id, ...formData }
        : formData;

      const response = await adminApiFetch(
        `${API_URLS.ADMIN.ADMIN_API}?action=${action}`,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        }
      );
      const data = await response.json();

      if (data.status === 'success') {
        await loadTemplates();
        handleCloseModal();
      } else {
        alert(data.message || 'Kayıt başarısız');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu şablonu silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await adminApiFetch(
        `${API_URLS.ADMIN.ADMIN_API}?action=deleteEmailTemplate`,
        {
          method: 'POST',
          body: JSON.stringify({ id })
        }
      );
      const data = await response.json();

      if (data.status === 'success') {
        await loadTemplates();
      } else {
        alert(data.message || 'Silme başarısız');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Silme sırasında hata oluştu');
    }
  };

  const handlePreview = (htmlContent) => {
    setPreviewHtml(htmlContent);
    setIsPreviewOpen(true);
  };

  const handleCopyTemplate = async (template) => {
    const newTemplate = {
      ...template,
      name: template.name + ' (Kopya)',
      id: undefined
    };
    setEditingTemplate(null);
    setFormData({
      name: newTemplate.name,
      subject: newTemplate.subject || '',
      html_content: newTemplate.html_content || '',
      description: newTemplate.description || '',
      is_active: true
    });
    setIsModalOpen(true);
  };

  const handleFormTemplateChange = async (formId, templateId) => {
    try {
      const response = await adminApiFetch(
        `${API_URLS.ADMIN.ADMIN_API}?action=updateFormTemplate`,
        {
          method: 'POST',
          body: JSON.stringify({ form_id: formId, template_id: templateId || null })
        }
      );
      const data = await response.json();

      if (data.status === 'success') {
        await loadForms();
      } else {
        alert(data.message || 'Güncelleme başarısız');
      }
    } catch (error) {
      console.error('Form template update error:', error);
      alert('Güncelleme sırasında hata oluştu');
    }
  };

  const filtered = templates.filter(t => 
    !query || 
    (t.name || '').toLowerCase().includes(query.toLowerCase()) ||
    (t.subject || '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 break-words">E-posta Şablonları</h1>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-1 sm:mt-2 break-words">E-posta şablonlarını oluşturun ve yönetin</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <Badge className="bg-blue-100 text-blue-700 w-fit text-xs sm:text-sm">{templates.length} şablon</Badge>
          <Button variant="outline" onClick={loadTemplates} className="w-full sm:w-auto flex items-center justify-center gap-2">
            <RefreshCcw className="w-4 h-4 flex-shrink-0"/>
            <span className="hidden sm:inline">Yenile</span>
            <span className="sm:hidden">Yenile</span>
          </Button>
          <Button onClick={() => handleOpenModal()} className="w-full sm:w-auto flex items-center justify-center gap-2">
            <Plus className="w-4 h-4 flex-shrink-0"/>
            <span>Yeni Şablon</span>
          </Button>
        </div>
      </div>

      {/* Variables Help Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start gap-2 sm:gap-3">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm sm:text-base text-blue-900 mb-2 break-words">Kullanılabilir Değişkenler</h3>
              <div className="text-xs sm:text-sm text-blue-800 space-y-1">
                <p className="break-words"><code className="bg-blue-100 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs">{'{{name}}'}</code> <span className="ml-1">- Alıcının adı</span></p>
                <p className="break-words"><code className="bg-blue-100 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs">{'{{email}}'}</code> <span className="ml-1">- Alıcının e-posta adresi</span></p>
                <p className="break-words"><code className="bg-blue-100 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs">{'{{company}}'}</code> <span className="ml-1">- Şirket adı</span></p>
                <p className="break-words"><code className="bg-blue-100 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs">{'{{date}}'}</code> <span className="ml-1">- Günün tarihi</span></p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form-Template Mapping */}
      <Card>
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="text-base sm:text-lg md:text-xl break-words">Form ve Şablon Eşleştirmeleri</CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
          <div className="space-y-3 sm:space-y-4">
            {forms.map((form) => (
              <div key={form.id} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm sm:text-base text-gray-900 break-words">{form.name}</h4>
                  {form.description && (
                    <p className="text-xs sm:text-sm text-gray-500 break-words mt-1">{form.description}</p>
                  )}
                  {form.template_name && (
                    <Badge variant="secondary" className="mt-2 text-[10px] sm:text-xs">
                      Şablon: {form.template_name}
                    </Badge>
                  )}
                </div>
                <div className="w-full sm:w-auto sm:min-w-[200px] sm:ml-4">
                  <select
                    value={form.email_template_id || ''}
                    onChange={(e) => handleFormTemplateChange(form.id, e.target.value)}
                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Şablon Seçilmedi</option>
                    {templates.filter(t => t.is_active).map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      <Card>
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            <CardTitle className="text-base sm:text-lg md:text-xl break-words">Şablonlar</CardTitle>
            <div className="relative w-full sm:w-auto sm:max-w-xs">
              <Input 
                value={query} 
                onChange={(e) => setQuery(e.target.value)} 
                placeholder="Şablon ara" 
                className="pl-8 text-xs sm:text-sm"
              />
              <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
          {loading ? (
            <div className="flex items-center justify-center h-32 sm:h-40 text-xs sm:text-sm text-gray-500">Yükleniyor...</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 sm:h-40 text-gray-500">
              <Mail className="w-10 h-10 sm:w-12 sm:h-12 mb-2 text-gray-300"/>
              <p className="text-xs sm:text-sm break-words">Henüz şablon eklenmemiş</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Şablon Adı</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Konu</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Açıklama</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filtered.map((template) => (
                      <tr key={template.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-blue-500 flex-shrink-0"/>
                            <span className="font-medium text-gray-900 break-words">{template.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                          {template.subject}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-sm max-w-xs truncate">
                          {template.description || '-'}
                        </td>
                        <td className="px-4 py-3">
                          {template.is_active ? (
                            <Badge className="bg-green-100 text-green-700 text-xs">Aktif</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Pasif</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(template.html_content)}
                              title="Önizle"
                              className="flex-shrink-0"
                            >
                              <Eye className="w-4 h-4"/>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyTemplate(template)}
                              title="Kopyala"
                              className="flex-shrink-0"
                            >
                              <Copy className="w-4 h-4"/>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenModal(template)}
                              title="Düzenle"
                              className="flex-shrink-0"
                            >
                              <Edit2 className="w-4 h-4 text-blue-600"/>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(template.id)}
                              title="Sil"
                              className="flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4 text-red-600"/>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filtered.map((template) => (
                  <div key={template.id} className="border rounded-lg p-3 sm:p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Mail className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5"/>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm text-gray-900 break-words">{template.name}</h3>
                          {template.is_active ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px] mt-1">Aktif</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px] mt-1">Pasif</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-3">
                      <div>
                        <span className="text-xs text-gray-500">Konu:</span>
                        <p className="text-xs sm:text-sm text-gray-700 break-words mt-0.5">{template.subject}</p>
                      </div>
                      {template.description && (
                        <div>
                          <span className="text-xs text-gray-500">Açıklama:</span>
                          <p className="text-xs text-gray-600 break-words mt-0.5">{template.description}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-1.5 pt-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(template.html_content)}
                        title="Önizle"
                        className="h-8 w-8 p-0 flex-shrink-0"
                      >
                        <Eye className="w-3.5 h-3.5"/>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyTemplate(template)}
                        title="Kopyala"
                        className="h-8 w-8 p-0 flex-shrink-0"
                      >
                        <Copy className="w-3.5 h-3.5"/>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenModal(template)}
                        title="Düzenle"
                        className="h-8 w-8 p-0 flex-shrink-0"
                      >
                        <Edit2 className="w-3.5 h-3.5 text-blue-600"/>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        title="Sil"
                        className="h-8 w-8 p-0 flex-shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-600"/>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-md md:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto p-3 sm:p-4 md:p-6">
          <DialogHeader className="mb-3 sm:mb-4">
            <div className="flex justify-between items-start gap-3">
              <DialogTitle className="text-base sm:text-lg md:text-xl break-words flex-1">
                {editingTemplate ? 'Şablonu Düzenle' : 'Yeni Şablon Oluştur'}
              </DialogTitle>
            </div>
          </DialogHeader>
          
          <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Şablon Adı *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Örn: Hoşgeldin E-postası"
                  className="text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  E-posta Konusu *
                </label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="Örn: Hoşgeldiniz {{name}}!"
                  className="text-sm sm:text-base"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Açıklama
              </label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Bu şablonun ne için kullanıldığını açıklayın"
                className="text-sm sm:text-base"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                HTML İçerik
              </label>
              <Textarea
                value={formData.html_content}
                onChange={(e) => setFormData({...formData, html_content: e.target.value})}
                placeholder="HTML içeriğini buraya yazın"
                className="font-mono text-xs sm:text-sm"
                rows={12}
              />
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1 break-words">
                HTML etiketleri ve {'{{değişkenler}}'} kullanabilirsiniz
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="rounded border-gray-300 flex-shrink-0"
              />
              <label htmlFor="is_active" className="text-xs sm:text-sm text-gray-700 break-words">
                Aktif
              </label>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
            <Button variant="outline" onClick={handleCloseModal} disabled={saving} className="w-full sm:w-auto">
              İptal
            </Button>
            <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
              {saving ? 'Kaydediliyor...' : (editingTemplate ? 'Güncelle' : 'Oluştur')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md md:max-w-2xl lg:max-w-4xl max-h-[90vh] p-3 sm:p-4 md:p-6">
          <DialogHeader className="mb-3 sm:mb-4">
            <DialogTitle className="text-base sm:text-lg md:text-xl break-words">Şablon Önizleme</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg p-3 sm:p-4 bg-white overflow-y-auto max-h-[60vh] sm:max-h-[70vh]">
            <div className="text-xs sm:text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml || '') }} />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)} className="w-full sm:w-auto">
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailTemplates;

